import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Briefcase, Tag } from "lucide-react";
import FadeIn from "../components/FadeIn";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import Parallax from "../components/Parallax";

function getOptimizedImage(url, width = 1600) {
  if (!url || !url.includes("assets.auxodev.dk")) return url;
  if (url.includes("/cdn-cgi/image/")) return url;
  return url.replace(
    "https://assets.auxodev.dk/",
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        // Query by the localized project_slug field for the current language
        const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/projects?project_slug.${lang}=${slug}`);
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setProject(data.items[0]);
            setLoading(false);
            return;
          }
        }

        // Fallback: fetch all projects and find the one that matches either da or en slug
        const fallbackRes = await fetch(`https://api.dev.auxo.dk/public/v2/content/projects`);
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          const found = fallbackData.items.find(item => {
            const pSlug = item.project_slug;
            if (typeof pSlug === "object") {
              return pSlug.da === slug || pSlug.en === slug;
            }
            return pSlug === slug || item.slug?._ === slug;
          });
          if (found) {
            setProject(found);
          } else {
            navigate(lang === "da" ? "/projekter" : "/en/projects");
          }
        }
      } catch (error) {
        console.error("Error fetching project:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug, lang, navigate]);

  if (loading) {
    return (
      <div className="bg-stone-50 text-stone-900 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-stone-500 font-mono text-xs uppercase tracking-[0.3em]">
          {t("project.detail.loading")}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-stone-50 text-stone-900 min-h-screen flex flex-col items-center justify-center p-6">
        <h2 className="text-2xl font-light uppercase tracking-widest mb-4">
          {t("project.detail.notFound")}
        </h2>
        <Link
          to={lang === "da" ? "/projekter" : "/en/projects"}
          className="text-xs uppercase tracking-wider text-stone-500 hover:text-stone-900 font-mono"
        >
          ← {t("project.detail.back")}
        </Link>
      </div>
    );
  }

  // Localized fields
  const pTitle = typeof project.title === "object" ? project.title[lang] : project.title;
  const pType = typeof project.project_type === "object" ? project.project_type[lang] : project.project_type;
  const pLoc = typeof project.location === "object" ? project.location[lang] : project.location;
  const pRole = typeof project.role === "object" ? project.role[lang] : project.role;
  const pDesc = typeof project.description === "object" ? project.description[lang] : project.description;
  const pScope = typeof project.scope_of_work === "object" ? project.scope_of_work[lang] : project.scope_of_work;

  const homePath = lang === "da" ? "/" : "/en";
  const projectsPath = lang === "da" ? "/projekter" : "/en/projects";
  const contactPath = lang === "da" ? "/kontakt" : "/en/contact";
  const servicesPath = lang === "da" ? "/ydelser" : "/en/services";
  const aboutPath = lang === "da" ? "/om-os" : "/en/about";

  // Process pDesc so the FIRST mention of CPH Collective links to the frontpage
  const linkStyle = "text-stone-900 font-medium underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 transition-colors";
  let processedDesc = pDesc;
  if (typeof pDesc === "string" && pDesc.includes("CPH Collective")) {
    processedDesc = pDesc.replace(
      "CPH Collective",
      `<a href="${homePath}" class="${linkStyle}">CPH Collective</a>`
    );
  } else if (typeof pDesc === "string") {
    const introSentence = lang === "da"
      ? `<p class="mb-4">Dette projekt er udført af <a href="${homePath}" class="${linkStyle}">CPH Collective</a> som ${pRole}.</p>`
      : `<p class="mb-4">This project was delivered by <a href="${homePath}" class="${linkStyle}">CPH Collective</a> as ${pRole}.</p>`;
    processedDesc = introSentence + pDesc;
  }

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen pt-24">
      {/* SEO Tags */}
      <SEO project={project} />

      {/* Back Navigation */}
      <div className="w-full px-6 md:px-12 lg:px-16 py-6 border-b border-stone-200">
        <Link
          to={projectsPath}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-stone-600 hover:text-stone-900 transition-colors font-mono"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("project.detail.back")}
        </Link>
      </div>

      {/* Hero Header Section */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Title & Type */}
            <div className="lg:col-span-8">
              <Parallax speed={0.04} type="text">
                <FadeIn direction="right">
                  <span className="text-xs uppercase tracking-[0.4em] text-stone-500 font-mono block mb-4">
                    {pType}
                  </span>
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight text-stone-900 uppercase leading-tight">
                    {pTitle}
                  </h1>
                </FadeIn>
              </Parallax>
            </div>

            {/* Quick Specs */}
            <div className="lg:col-span-4">
              <FadeIn direction="left" delay={150}>
                <div className="bg-white border border-stone-200 shadow-sm p-8 space-y-6">
                  <h3 className="text-xs uppercase tracking-[0.3em] text-stone-900 font-mono border-b border-stone-200 pb-4">
                    {t("project.detail.specs")}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-stone-500 shrink-0 mt-0.5" />
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-stone-500 font-mono">
                          {t("project.detail.location")}
                        </span>
                        <span className="text-sm text-stone-700">{pLoc}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase className="w-4 h-4 text-stone-500 shrink-0 mt-0.5" />
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-stone-500 font-mono">
                          {t("project.detail.role")}
                        </span>
                        <span className="text-sm text-stone-700">{pRole}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Tag className="w-4 h-4 text-stone-500 shrink-0 mt-0.5" />
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-stone-500 font-mono">
                          {t("project.detail.type")}
                        </span>
                        <span className="text-sm text-stone-700">{pType}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Large Cinematic Image */}
      <section className="w-full h-[60vh] md:h-[80vh] overflow-hidden border-b border-stone-200">
        <FadeIn direction="up" className="w-full h-full">
          <Parallax speed={-0.1} type="background" className="w-full h-full">
            <img
              src={getOptimizedImage(project.images?.[0], 1800)}
              alt={`${pTitle} – ${pType} | CPH Collective`}
              className="w-full h-full object-cover object-center"
            />
          </Parallax>
        </FadeIn>
      </section>

      {/* Detailed Description & Scope */}
      <section className="py-24 md:py-32">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            {/* Left: Description */}
            <div className="lg:col-span-7">
              <FadeIn direction="right">
                <h2 className="text-xs uppercase tracking-[0.3em] text-stone-500 font-mono mb-6">
                  {t("project.detail.overview")}
                </h2>
                <div
                  className="prose max-w-none text-stone-700 text-lg font-light leading-relaxed space-y-6"
                  dangerouslySetInnerHTML={{ __html: processedDesc }}
                />
              </FadeIn>
            </div>

            {/* Right: Scope of Work */}
            <div className="lg:col-span-5">
              <FadeIn direction="left" delay={150}>
                <div className="bg-white border border-stone-200 shadow-sm p-8 md:p-12">
                  <h3 className="text-xs uppercase tracking-[0.3em] text-stone-900 font-mono mb-6 border-b border-stone-200 pb-4">
                    {t("project.detail.scope")}
                  </h3>
                  <div
                    className="space-y-4 text-stone-700 text-sm font-light leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: pScope }}
                  />
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Block */}
      <section className="bg-stone-100 py-20 border-t border-stone-200">
        <div className="w-full px-6 md:px-12 lg:px-16 text-center">
          <div className="max-w-3xl mx-auto">
            <FadeIn direction="up">
              <h2 className="text-2xl md:text-3xl font-light uppercase tracking-wider text-stone-900 mb-6">
                {t("project.detail.cta.title")}
              </h2>
              <p className="text-stone-600 text-sm md:text-base font-light mb-8 leading-relaxed">
                {t("project.detail.cta.text")}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={contactPath}
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white bg-stone-900 hover:bg-stone-800 transition-all duration-500 px-8 py-4 font-semibold font-mono"
                >
                  {t("cta.requestQuote")}
                </Link>
                <Link
                  to={servicesPath}
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-900 hover:bg-stone-200 border border-stone-300 transition-all duration-300 px-8 py-4 font-mono"
                >
                  <span>{lang === "da" ? "Se relaterede ydelser" : "View related services"}</span>
                </Link>
                <Link
                  to={aboutPath}
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-stone-900 hover:bg-stone-200 border border-stone-300 transition-all duration-300 px-8 py-4 font-mono"
                >
                  <span>{lang === "da" ? "Om CPH Collective" : "About CPH Collective"}</span>
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
